import { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { useLanguage } from '../../../../context/LanguageContext';

interface QueryExportDialogProps {
    open: boolean;
    defaultEmail?: string;
    loading?: boolean;
    error?: string | null;
    onClose: () => void;
    onConfirm: (params: { notify: string[]; canal: string }) => void;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const parseEmails = (value: string) => (
    value
        .split(/[\n,;]+/)
        .map(email => email.trim())
        .filter(Boolean)
);

function QueryExportDialog({
    open,
    defaultEmail = '',
    loading = false,
    error,
    onClose,
    onConfirm,
}: QueryExportDialogProps) {
    const { t } = useLanguage();
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const [emailText, setEmailText] = useState(defaultEmail);
    const [canal, setCanal] = useState('');
    const [validationError, setValidationError] = useState<string | null>(null);

    const channelOptions = useMemo(() => [
        { value: '', label: t('pages.clients.queryBuilder.exportDialog.allChannels') },
        { value: 'email', label: t('pages.clients.queryBuilder.exportDialog.channels.email') },
        { value: 'sms', label: t('pages.clients.queryBuilder.exportDialog.channels.sms') },
        { value: 'wallet', label: t('pages.clients.queryBuilder.exportDialog.channels.wallet') },
        { value: 'courrier', label: t('pages.clients.queryBuilder.exportDialog.channels.courrier') },
    ], [t]);

    useEffect(() => {
        if (!open) return;
        setEmailText(defaultEmail);
        setCanal('');
        setValidationError(null);
    }, [open, defaultEmail]);

    const emails = useMemo(() => parseEmails(emailText), [emailText]);

    const handleConfirm = () => {
        if (emails.length === 0) {
            setValidationError(t('pages.clients.queryBuilder.exportDialog.emailRequired'));
            return;
        }

        const invalidEmail = emails.find(email => !EMAIL_PATTERN.test(email));
        if (invalidEmail) {
            setValidationError(t('pages.clients.queryBuilder.exportDialog.invalidEmail', { email: invalidEmail }));
            return;
        }

        setValidationError(null);
        onConfirm({ notify: emails, canal });
    };

    return (
        <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth fullScreen={fullScreen}>
            <DialogTitle>{t('pages.clients.queryBuilder.exportDialog.title')}</DialogTitle>

            <DialogContent dividers>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Alert severity="info">
                        {t('pages.clients.queryBuilder.exportDialog.infoMessage')}
                    </Alert>

                    {(error || validationError) && (
                        <Alert severity="error">
                            {validationError || error}
                        </Alert>
                    )}

                    <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                            {t('pages.clients.queryBuilder.exportDialog.requiredFieldsTitle')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {t('pages.clients.queryBuilder.exportDialog.requiredFieldsList')}
                        </Typography>
                    </Box>

                    <FormControl size="small" fullWidth>
                        <InputLabel>{t('pages.clients.queryBuilder.exportDialog.channelLabel')}</InputLabel>
                        <Select
                            value={canal}
                            label={t('pages.clients.queryBuilder.exportDialog.channelLabel')}
                            onChange={(event) => setCanal(event.target.value)}
                            disabled={loading}
                        >
                            {channelOptions.map(option => (
                                <MenuItem key={option.value || 'all'} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField
                        label={t('pages.clients.queryBuilder.exportDialog.emailLabel')}
                        value={emailText}
                        onChange={(event) => setEmailText(event.target.value)}
                        disabled={loading}
                        fullWidth
                        multiline
                        minRows={2}
                        helperText={t('pages.clients.queryBuilder.exportDialog.emailHelper')}
                    />
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose} disabled={loading}>
                    {t('pages.clients.queryBuilder.exportDialog.back')}
                </Button>
                <Button
                    onClick={handleConfirm}
                    variant="contained"
                    disabled={loading}
                    startIcon={loading ? <CircularProgress color="inherit" size={16} /> : undefined}
                >
                    {loading ? t('pages.clients.queryBuilder.exportDialog.submitting') : t('pages.clients.queryBuilder.exportDialog.confirm')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default QueryExportDialog;
